import { useCallback, MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { configSelectors } from 'store/reducers/configSlice';
import { addSplitRow, removeSplitRow, updateReturnItem } from 'store/reducers/returnSlice';
import { ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { CellInputRefs } from './useKeyboardNavigation';

// type CellInputElement = HTMLInputElement | HTMLSelectElement | HTMLButtonElement | HTMLDivElement | null;

// interface CellInputRefs {
//     [key: string]: CellInputElement | null;
// }

interface UseSplitRowManagementOptions {
    // cellInputRefs: MutableRefObject<CellInputRefs>;
    cellInputRefs?: CellInputRefs;
}

export const useSplitRowManagement = ({ cellInputRefs }: UseSplitRowManagementOptions) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const misPickReasonCodeIdSet = useAppSelector(configSelectors.selectMisPickReasonCodeIds);
    const misPickReasonCodeIds = useMemo(() => Array.from(misPickReasonCodeIdSet), [misPickReasonCodeIdSet]);

    const handleAddSplitItem = useCallback(
        (
            orderNumber: string,
            item: ReturnItem,
            itemId: number | string,
            orderline: ReturnOrderLine,
            sourceReturnId?: number | null,
        ) => {
            if (!orderNumber || !orderline?.sku || !orderline || !itemId) {
                toast.error(t('splitItemError', { orderNumber: orderNumber, sku: orderline.sku }));
                return;
            }

            // 1. Set base item's quantityInReturn to 1 if it's 0
            if (!item.quantityInReturn || item.quantityInReturn === 0) {
                dispatch(
                    updateReturnItem({
                        orderNumber: orderNumber,
                        sku: orderline.sku,
                        orderLineNumber: orderline.orderLineNumber ?? '',
                        itemId: itemId,
                        property: 'quantityInReturn',
                        value: 1,
                        sourceReturnId,
                    }),
                );
            }

            // 2. Add a split row with quantityInReturn = 1
            const uniqueId = uuidv4();
            dispatch(
                addSplitRow({
                    orderNumber,
                    itemId,
                    sku: orderline.sku,
                    orderLineNumber: orderline.orderLineNumber ?? '',
                    splitId: uniqueId,
                    sourceReturnId,
                    splitItem: {
                        quantityInReturn: 1,
                        percent: item.percent ?? 1.0,
                        comment: '',
                        returnToStock: true,
                        batchReturnDestinationId: item.batchReturnDestinationId,
                    },
                }),
            );
        },
        [dispatch, t],
    );

    const handleRemoveSplit = useCallback(
        (
            sku: string,
            orderNumber: string,
            orderLineNumber: string,
            id?: string | number,
            rowIndex?: number,
            colIndex?: number,
            cellRefPrefix?: string,
            sourceReturnId?: number | null,
        ) => {
            if (!orderNumber || !id) {
                toast.error(
                    t('removeSplitError', {
                        orderNumber: orderNumber ?? 'unknown',
                        split: sku ?? 'unknown',
                    }),
                );
                return;
            }
            dispatch(
                removeSplitRow({
                    orderNumber,
                    sku,
                    orderLineNumber,
                    splitId: id,
                    sourceReturnId,
                    misPickReasonCodeIds,
                }),
            );

            // Focus the previous row (base row or row above) after deletion
            if (rowIndex !== undefined && colIndex !== undefined && rowIndex > 0) {
                setTimeout(() => {
                    const prefix = cellRefPrefix ?? '';
                    const prevRowKey = `${prefix}${rowIndex - 1}-${colIndex}`;
                    cellInputRefs?.current[prevRowKey]?.focus();
                }, 0);
            }
        },
        [dispatch, t, cellInputRefs, misPickReasonCodeIds],
    );

    return {
        handleAddSplitItem,
        handleRemoveSplit,
    };
};

export default useSplitRowManagement;
